import { ReactElement, KeyboardEvent } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {
  ISession,
  ESessionType,
  ISessionGroup,
} from "logic/src/objects/session/type";
import Session from "logic/src/objects/session/model";
import { trim } from "lodash";
import cls from "classnames";
import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useRef, useState, useEffect, useMemo } from "react";
import { useClickAway, useDebounceFn } from "ahooks";
import "./index.scss";
import { genFuzzyMatchingReg } from "logic/src/utils";
import { Input, InputRef, Tooltip, Modal, App } from "antd";
import SJButton from "@/components/SJButton";
import { ReactComponent as FindIcon } from "@/components/icons/Find.svg";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/close.svg";
import { ReactComponent as UpIcon } from "logic/src/components/icons/up.svg";
import { ReactComponent as UpFillIcon } from "logic/src/components/icons/upFill.svg";
import { ReactComponent as EditIcon } from "logic/src/components/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "logic/src/components/icons/delete.svg";
import { SESSION_TYPE_MAP } from "logic/src/objects/session/constants";
import { sendCLK } from "logic/src/utils/eventTrack";

const { confirm } = Modal;

const Title = observer(
  ({
    item,
    query,
    isVideoType,
  }: {
    item: ISession;
    query?: string;
    isVideoType?: boolean;
  }) => {
    const title = item.title
      ? item.title
      : isVideoType
        ? "视频理解_" + moment().format("YYYY-MM-DD HH:mm:ss")
        : "";
    const reg = genFuzzyMatchingReg(query || "");
    let newTitle: (string | ReactElement)[] = [title];
    if (reg) {
      const matches = title.match(reg);
      if (matches) {
        newTitle = matches.slice(1).map((s, index) =>
          index % 2 === 1 ? (
            <span key={index} className="highlight">
              {s}
            </span>
          ) : (
            s
          ),
        );
      }
    }
    return <>{newTitle}</>;
  },
);

const SessionList = ({
  isVideoType,
  isLogin,
  isFetchData,
  query,
  sessionList,
  session,
  onPinned,
  onUnPinned,
  onChange,
  onDelete,
  onEditName,
  onNewSession,
  onQuerySession,
  onLoadSession,
}: {
  isVideoType?: boolean;
  isLogin: boolean;
  isFetchData?: boolean;
  query?: string;
  sessionList: ISessionGroup[];
  session: Session;
  onPinned: (id: string) => void;
  onUnPinned: (id: string) => void;
  onChange: (id: string, tempId?: string) => void;
  onDelete: (id: string) => void;
  onEditName?: (name: string, id?: string) => void;
  onNewSession: () => void;
  onQuerySession: (query: string) => void;
  onLoadSession?: () => void;
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  // const [searchValue, setSearchValue] = useState<string>("");
  const editSessionRef = useRef<ISession>();
  const [editValue, setEditValue] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [editting, setEditting] = useState("");
  const [editedSession, setEditedSession] = useState<ISession | null>(null); // 正在编辑的session

  const inputRef = useRef<InputRef>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef<number>(0);

  const handleQuerySession = _.debounce(inputQuery => {
    if (!inputQuery.length && searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    onQuerySession?.(inputQuery);
  }, 300);

  const { run: handleListScroll } = useDebounceFn(
    () => {
      if (isFetchData || !containerRef.current) return;

      if (containerRef.current.scrollTop <= lastScrollTopRef.current) {
        lastScrollTopRef.current = containerRef.current.scrollTop;
        return;
      }

      lastScrollTopRef.current = containerRef.current.scrollTop;

      if (containerRef.current) {
        const distance =
          containerRef.current.scrollHeight -
          containerRef.current.scrollTop -
          containerRef.current.clientHeight;

        distance < 250 && onLoadSession?.();
      }
    },
    {
      wait: 100,
    },
  );

  useClickAway(
    () => {
      setShowEditor(false);
      // TODO: input有时不存,后面看看
    },
    inputRef.current?.input || document.querySelector("input"),
  );

  const handleEdit = (s: ISession) => {
    sendCLK("click_rename_button", "点击重命名按钮");
    editSessionRef.current = s;
    setEditedSession(s);
    setEditting(s.session_id);
    setEditValue(s.title);
    // 先触发关闭原编辑态的item
    setTimeout(() => {
      setShowEditor(true);
    }, 16);
  };

  const closeEditor = () => {
    setShowEditor(false);
  };

  const confirmEditor = async () => {
    if (trim(editValue).length <= 0) return false;
    await onEditName?.(
      editValue,
      editedSession?.session_id === session.session_id
        ? undefined
        : editedSession?.session_id,
    );
    setShowEditor(false);
  };

  const handleDelete = (session: ISession) => {
    modal.confirm({
      title: t("sensechat-43"), // 确定删除该历史对话吗?
      okText: t("sensechat-57"), // 确定
      // icon: <span></span>,
      okType: "primary",
      autoFocusButton: null,
      centered: true,
      okButtonProps: {
        className: "sj-button short-btn",
        size: "small",
        type: "primary",
      },
      cancelText: t("sensechat-21"), // 取消
      cancelButtonProps: {
        className: "sj-button short-btn",
        size: "small",
        type: "dashed",
      },
      onOk() {
        onDelete(session.session_id);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const handleInputChange = (event: any) => {
    const val = event.target.value ?? "";
    setEditValue(val);
  };

  const handleChangeSession = (id: string, tempId?: string) => {
    if (editting !== id) setShowEditor(false);
    if (session?.session_id !== id) {
      onChange(id, tempId);
    }
  };

  const createNewSession = () => {
    setShowEditor(false);
    onNewSession();
  };

  const renderTitle = (title: string): (string | ReactElement)[] => {
    const reg = genFuzzyMatchingReg(query || "");
    let newTitle: (string | ReactElement)[] = [title];
    if (reg) {
      const matches = title.match(reg);
      if (matches) {
        newTitle = matches.slice(1).map((s, index) =>
          index % 2 === 1 ? (
            <span key={index} className="highlight">
              {s}
            </span>
          ) : (
            s
          ),
        );
      }
    }
    return newTitle;
  };

  const keyPre = query ? "Q-" : "";
  return (
    <>
      <SJButton
        block
        type="primary"
        // style={{ background: chatItem.addNewBgd, color: chatItem.color }}
        className="sider-add-btn"
        onClick={createNewSession}
      >
        <PlusOutlined
          className="folded-new-icon"
          style={{
            width: "12px",
            color: "#ffffff",
            fontWeight: "500",
          }}
        />
        {/* 发起新对话 */}
        <span className="add-font">{t("sensechat-44")}</span>
      </SJButton>
      {isLogin && (
        <div className="chat-history-search">
          <FindIcon
            style={{
              color: query ? "#333333" : "#797D8B",
              cursor: "pointer",
            }}
          />
          <input
            ref={searchInputRef}
            placeholder={t("sensechat-314") as string} // "搜索历史话题"
            // value={query}
            onChange={e => {
              sendCLK("search_history_topics", "触发历史话题搜索");
              handleQuerySession(e.target.value);
            }}
          />
          {!!query && (
            <CloseIcon
              className="close-btn"
              onClick={() => handleQuerySession("")}
            />
          )}
        </div>
      )}
      <div
        className="chat-history"
        ref={containerRef}
        onScroll={handleListScroll}
      >
        {sessionList.map((item: ISessionGroup, index: number) => (
          <div key={`${keyPre}${item.type}`}>
            {!query && !!item.session_list?.length && (
              <p className="list-title">{SESSION_TYPE_MAP[item.type]}</p>
            )}
            <div>
              {item.session_list.map((sessionItem: ISession) =>
                sessionItem.session_id === session?.session_id && showEditor ? (
                  <div
                    key={`${keyPre}${sessionItem.session_id}edit`}
                    className="editting-style"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Input
                      ref={inputRef}
                      key={sessionItem.session_id + "input"}
                      className="edit-input"
                      value={editValue}
                      onChange={handleInputChange}
                      // onBlur={handleInputBlur}
                      autoFocus
                      onKeyDown={(e: KeyboardEvent) => {
                        if (e.key === "Enter") {
                          confirmEditor();
                        } else if (e.key === "Escape") {
                          closeEditor();
                        }
                      }}
                    ></Input>
                    <>
                      <CheckOutlined
                        style={{ opacity: trim(editValue) ? "1" : ".2" }}
                        onClick={confirmEditor}
                      />
                      <CloseOutlined onClick={closeEditor} />
                    </>
                  </div>
                ) : (
                  <div
                    key={`${keyPre}${sessionItem.session_id}`}
                    className={cls(
                      "chat-history-item",
                      sessionItem.session_id === session?.session_id &&
                        "selected",
                    )}
                    onClick={() =>
                      handleChangeSession(
                        sessionItem.session_id,
                        sessionItem.tempId,
                      )
                    }
                  >
                    <span
                      className="chat-history-item-content"
                      style={{
                        color:
                          sessionItem.session_id === session?.session_id
                            ? "#5D63C5"
                            : "",
                      }}
                    >
                      <Title
                        item={sessionItem}
                        isVideoType={isVideoType}
                        query={query}
                      />
                    </span>
                    {sessionItem.session_id && (
                      <div className="chat-history-item-icons">
                        {item.type == ESessionType.top || sessionItem.is_top ? (
                          <Tooltip
                            placement="top"
                            trigger="hover"
                            color="#242424"
                            title="取消置顶"
                          >
                            <UpFillIcon
                              onClickCapture={e => {
                                e.stopPropagation();
                                onUnPinned(sessionItem.session_id);
                              }}
                              className="chat-history-item-icon"
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip
                            placement="top"
                            trigger="hover"
                            color="#242424"
                            title="置顶"
                          >
                            <UpIcon
                              onClickCapture={e => {
                                e.stopPropagation();
                                sendCLK("click_pin_button", "点击置顶按钮");
                                onPinned(sessionItem.session_id);
                              }}
                              className="chat-history-item-icon"
                            />
                          </Tooltip>
                        )}
                        <Tooltip
                          placement="top"
                          trigger="hover"
                          color="#242424"
                          title="重命名"
                        >
                          <EditIcon
                            onClickCapture={() => handleEdit(sessionItem)}
                            className="chat-history-item-icon"
                          />
                        </Tooltip>
                        <Tooltip
                          placement="top"
                          trigger="hover"
                          color="#242424"
                          title="删除"
                        >
                          <DeleteIcon
                            onClickCapture={e => {
                              e.stopPropagation();
                              handleDelete(sessionItem);
                            }}
                            className="chat-history-item-icon"
                          />
                        </Tooltip>
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        ))}
        {isFetchData && (
          <div className="session-loading-wrap">
            <LoadingOutlined spin style={{ color: "#C5C5C5", fontSize: 20 }} />
          </div>
        )}
      </div>
    </>
  );
};
export default observer(SessionList);
